class FeaturedLook extends HTMLElement {
  constructor() {
    super();
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleContainerClick = this.handleContainerClick.bind(this);
    this.boundHandleBulkAddClick = this.handleBulkAddClick.bind(this);
    this.hoverCloseTimeout = null;
    this.hoverCloseDelay = 120;
    this.ignoreDocumentClickUntil = 0;
  }

  connectedCallback() {
    this.container = this.querySelector('.featured-look__image');
    if (!this.container) return;

    this.container.addEventListener('click', this.boundHandleContainerClick);
    document.addEventListener('click', this.boundHandleDocumentClick);
    window.addEventListener('resize', this.boundHandleResize);

    this.bulkAddButton = this.querySelector('[data-featured-look-bulk-add]');
    if (this.bulkAddButton) {
      this.bulkAddButton.addEventListener('click', this.boundHandleBulkAddClick);
    }

    requestAnimationFrame(() => {
      if (window.matchMedia('(hover: hover)').matches) this.setupHover();
    });
  }

  disconnectedCallback() {
    this.clearHoverCloseTimeout();
    if (this.container) {
      this.container.removeEventListener('click', this.boundHandleContainerClick);
    }
    if (this.bulkAddButton) {
      this.bulkAddButton.removeEventListener('click', this.boundHandleBulkAddClick);
    }
    if (this.wraps) {
      this.wraps.forEach((wrap) => {
        wrap.removeEventListener('mouseenter', this.boundHandleWrapEnter);
        wrap.removeEventListener('mouseleave', this.boundHandleWrapLeave);
      });
    }
    if (this.triggers) {
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('mouseenter', this.boundHandleTriggerEnter);
        trigger.removeEventListener('mouseleave', this.boundHandleTriggerLeave);
      });
    }
    if (this.popovers) {
      this.popovers.forEach((pop) => {
        pop.removeEventListener('mouseenter', this.boundHandlePopoverEnter);
        pop.removeEventListener('mouseleave', this.boundHandlePopoverLeave);
      });
    }
    document.removeEventListener('click', this.boundHandleDocumentClick);
    window.removeEventListener('resize', this.boundHandleResize);
  }

  setupHover() {
    this.wraps = Array.from(this.querySelectorAll('.hotspot-wrap'));
    this.triggers = Array.from(this.querySelectorAll('[data-featured-look-trigger]'));
    this.popovers = Array.from(this.querySelectorAll('.hotspot-popover'));
    this.boundHandleWrapEnter = (e) => this.handleWrapEnter(e);
    this.boundHandleWrapLeave = () => this.handleWrapLeave();
    this.boundHandleTriggerEnter = (e) => this.handleTriggerEnter(e);
    this.boundHandleTriggerLeave = () => this.handleWrapLeave();
    this.boundHandlePopoverEnter = (e) => this.handlePopoverEnter(e);
    this.boundHandlePopoverLeave = () => this.handleWrapLeave();

    this.wraps.forEach((wrap) => {
      wrap.addEventListener('mouseenter', this.boundHandleWrapEnter);
      wrap.addEventListener('mouseleave', this.boundHandleWrapLeave);
    });
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', this.boundHandleTriggerEnter);
      trigger.addEventListener('mouseleave', this.boundHandleTriggerLeave);
    });
    this.popovers.forEach((pop) => {
      pop.addEventListener('mouseenter', this.boundHandlePopoverEnter);
      pop.addEventListener('mouseleave', this.boundHandlePopoverLeave);
    });
  }

  handleTriggerEnter(e) {
    this.clearHoverCloseTimeout();
    const trigger = e.currentTarget;
    if (trigger) this.openPopover(trigger);
  }

  clearHoverCloseTimeout() {
    if (this.hoverCloseTimeout) {
      clearTimeout(this.hoverCloseTimeout);
      this.hoverCloseTimeout = null;
    }
  }

  handleWrapEnter(e) {
    this.clearHoverCloseTimeout();
    const trigger = e.currentTarget.querySelector('[data-featured-look-trigger]');
    if (trigger) this.openPopover(trigger);
  }

  handleWrapLeave() {
    this.clearHoverCloseTimeout();
    this.hoverCloseTimeout = setTimeout(() => this.closeAllPopovers(), this.hoverCloseDelay);
  }

  handlePopoverEnter(e) {
    this.clearHoverCloseTimeout();
    e.currentTarget.closest('.hotspot-wrap')?.classList.add('is-active');
  }

  handlePopoverLeave() {
    this.handleWrapLeave();
  }

  closeAllPopovers() {
    this.querySelectorAll('.hotspot-popover:not([hidden])').forEach((pop) => {
      pop.setAttribute('hidden', '');
      pop.style.removeProperty('--popover-viewport-offset');
    });
    this.querySelectorAll('[data-featured-look-trigger][aria-expanded="true"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.hotspot-wrap').forEach((wrap) => {
      wrap.classList.remove('is-active');
    });
  }

  clampPopoverToImage(popover) {
    if (!popover || popover.hasAttribute('hidden') || !this.container) return;
    requestAnimationFrame(() => {
      const rect = popover.getBoundingClientRect();
      const imageRect = this.container.getBoundingClientRect();
      let offset = 0;
      if (rect.left < imageRect.left) offset = imageRect.left - rect.left;
      if (rect.right > imageRect.right) offset = offset - (rect.right - imageRect.right);
      popover.style.setProperty('--popover-viewport-offset', offset + 'px');
    });
  }

  openPopover(trigger) {
    const id = trigger.getAttribute('aria-controls');
    if (!id) return;
    this.closeAllPopovers();
    const popover = this.querySelector('#' + id);
    if (popover) {
      popover.removeAttribute('hidden');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest('.hotspot-wrap')?.classList.add('is-active');
      this.clampPopoverToImage(popover);
      this.ignoreDocumentClickUntil = Date.now() + 350;
    }
  }

  handleContainerClick(e) {
    const trigger = e.target.closest('[data-featured-look-trigger]');
    if (trigger) {
      e.preventDefault();
      const id = trigger.getAttribute('aria-controls');
      const popover = id ? this.querySelector('#' + id) : null;
      const isOpen = popover && !popover.hasAttribute('hidden');
      if (isOpen) this.closeAllPopovers();
      else this.openPopover(trigger);
      return;
    }
    if (e.target.closest('.hotspot-popover')) return;
    this.closeAllPopovers();
  }

  handleDocumentClick(e) {
    if (this.contains(e.target)) return;
    if (Date.now() < this.ignoreDocumentClickUntil) return;
    this.closeAllPopovers();
  }

  handleResize() {
    const open = this.querySelector('.hotspot-popover:not([hidden])');
    if (open) this.clampPopoverToImage(open);
  }

  getBulkItems() {
    const wrapper = this.querySelector('[data-featured-look-bulk-ids]');
    if (!wrapper) return [];
    const raw = wrapper.getAttribute('data-featured-look-bulk-ids');
    if (!raw || !raw.trim()) return [];
    return raw
      .trim()
      .split(',')
      .map((id) => ({ id: parseInt(id, 10), quantity: 1 }))
      .filter((item) => !Number.isNaN(item.id) && item.id > 0);
  }

  setBulkBusy(busy) {
    if (!this.bulkAddButton) return;
    this.bulkAddButton.toggleAttribute('disabled', busy);
    this.bulkAddButton.classList.toggle('loading', busy);
  }

  async handleBulkAddClick() {
    const items = this.getBulkItems();
    if (!items.length) return;

    const cartAddUrl = (window.routes?.cart_add_url || '/cart/add') + '.js';
    this.setBulkBusy(true);
    try {
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      const data = await res.json().catch(() => ({}));
      if (res.status !== 200) throw new Error(data.description || data.message || 'Cart request failed');

      const sourceItems = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : data?.id
            ? [data]
            : [];
      const detailItems = sourceItems.map((item) => ({
        variantId: item.variant_id ?? item.id,
        productId: item.product_id,
        quantity: item.quantity ?? 1,
        productTitle: item.product_title ?? item.title ?? '',
        title: item.title ?? item.product_title ?? ''
      }));

      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { items: detailItems } }));

      const drawer = document.getElementById('drawer-cart');
      if (drawer && typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers) {
        Woolman.ModalsAndDrawers.showModalOrDrawer('drawer-cart');
      }
    } catch (e) {
      console.error('Featured look bulk add failed', e);
    } finally {
      this.setBulkBusy(false);
    }
  }
}

customElements.define('featured-look', FeaturedLook);
